/**
 * Shared top bar for expert-review screens — back button and overflow menu sheet.
 *
 * Belongs here: back + menu chrome wired to strategy/preferences exit sheets.
 * Does not belong here: stack leave for in-flow back (callers pass onBack).
 * Menu Exit clears both sessions then exitExpertReview (native leave by `source`).
 */
import BackButton from '../../../../../components/ui-components/BackButton'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { ExpertReviewEventIds } from '../../analytics/expertReviewEvents'
import { PreferencesMenuSheet } from '../../preferences/ui/sheets/MenuSheet'
import { StrategyQuestionnaireMenuSheet } from '../../strategy-questionnaire/ui/MenuSheet'
import { useStrategyQuestionnaireContext } from '../../strategy-questionnaire/flow-engine/StrategyQuestionnaireProvider'
import { usePreferencesContext } from '../../preferences/flow-engine/PreferencesProvider'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'

export const EXPERT_REVIEW_MORE_OPTIONS_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="5" height="18" viewBox="0 0 5 18" fill="none">
  <path d="M2 4C3.10457 4 4 3.10457 4 2C4 0.895427 3.10457 0 2 0C0.895426 0 0 0.895427 0 2C0 3.10457 0.895426 4 2 4Z" fill="#232323"/>
  <path d="M2 11C3.10457 11 4 10.1046 4 9C4 7.89543 3.10457 7 2 7C0.895426 7 0 7.89543 0 9C0 10.1046 0.895426 11 2 11Z" fill="#232323"/>
  <path d="M2 18C3.10457 18 4 17.1045 4 16C4 14.8955 3.10457 14 2 14C0.895426 14 0 14.8955 0 16C0 17.1045 0.895426 18 2 18Z" fill="#232323"/>
</svg>`

export type ExpertReviewTopBarMenu = 'strategy' | 'preferences'

interface ExpertReviewTopBarProps {
  onBack?: () => void
  /** When set, shows more-options and opens the matching exit sheet. */
  menu?: ExpertReviewTopBarMenu
  /** ScreenName for back / menu hits — caller owns the surface. */
  analyticsScreen: string
  testLocatorBack?: string
  testLocatorMenu?: string
  className?: string
}

/** Shared back + more-options row. Either side is optional (spacer when omitted). */
export function ExpertReviewTopBar({
  onBack,
  menu,
  analyticsScreen,
  testLocatorBack,
  testLocatorMenu,
  className = ''
}: ExpertReviewTopBarProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { clearSession: clearStrategySession } = useStrategyQuestionnaireContext()
  const { clearSession: clearPreferencesSession } = usePreferencesContext()
  const { exitExpertReview } = useExpertReviewNavigation()

  function handleBackClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: ExpertReviewEventIds.Back,
        buttonLabel: 'Back'
      }
    })
    onBack?.()
  }

  /** Menu Exit — clear sessions then leave the feature. */
  function handleExitFeature() {
    clearStrategySession()
    clearPreferencesSession()
    exitExpertReview()
  }

  function handleMenuClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: ExpertReviewEventIds.Menu,
        buttonLabel: 'More options'
      }
    })
    if (menu === 'preferences') {
      openBottomSheet(
        <PreferencesMenuSheet
          analyticsScreen={analyticsScreen}
          onDismiss={closeBottomSheet}
          onExitPreferences={handleExitFeature}
        />
      )
      return
    }
    openBottomSheet(
      <StrategyQuestionnaireMenuSheet
        analyticsScreen={analyticsScreen}
        onDismiss={closeBottomSheet}
        onExitAssessment={handleExitFeature}
      />
    )
  }

  return (
    <div className={`flex h-6 items-center justify-between ${className}`.trim()}>
      {onBack != null ? (
        <BackButton fill="#000000" size={24} testLocator={testLocatorBack} onClick={handleBackClick} />
      ) : (
        <span className="h-6 w-6 shrink-0" aria-hidden />
      )}
      {menu != null ? (
        <button
          type="button"
          onClick={handleMenuClick}
          aria-label="More options"
          className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0"
          {...(testLocatorMenu ? { 'test-locator': testLocatorMenu } : {})}
        >
          <RenderHTML innerHTMLText={EXPERT_REVIEW_MORE_OPTIONS_ICON} />
        </button>
      ) : (
        <span className="h-6 w-6 shrink-0" aria-hidden />
      )}
    </div>
  )
}
